import { z } from 'zod'

// image — 사진 줄(스튜디오 인트로). 서버가 who 'image' 를 받기 전엔 narration 줄에 image 만 얹혀 온다(backend10.md B10-5)
const openingLineSchema = z.looseObject({
  who: z.enum(['char', 'user', 'narration', 'image']),
  text: z.string(),
  charId: z.string().optional(),
  image: z.string().optional(),
})

/** Bootstrap의 공개 작품 중 화면에 쓰는 필드를 경계에서 확인한다. */
export const liveWorkSchema = z.looseObject({
  id: z.string().trim().min(1),
  name: z.string().trim().min(1),
  kind: z.enum(['character', 'story']),
  lane: z.enum(['a', 'f', 'm']).optional(),
  target: z.string().optional(),
  genre: z.string().optional(),
  subtitle: z.string().optional(),
  description: z.string().optional(),
  official: z.boolean().optional(),
  creator: z
    .looseObject({ id: z.string(), name: z.string() })
    .nullable()
    .optional(),
  style: z.looseObject({ rating: z.string().optional() }).optional(),
  adultOnly: z.boolean().optional(),
  rating: z.string().optional(),
  image: z.string().optional(),
  introGif: z.string().optional(),
  titleImage: z.string().optional(),
  images: z.record(z.string(), z.string()).optional(),
  cast: z
    .array(
      z.looseObject({
        name: z.string(),
        id: z.string().optional(),
        age: z.union([z.number(), z.string()]).nullable().optional(),
        role: z.string().optional(),
        image: z.string().optional(),
      })
    )
    .optional(),
  starts: z
    .array(
      z.looseObject({
        id: z.string().optional(),
        name: z.string(),
        place: z.string().optional(),
        intro: z.looseObject({ lines: z.array(openingLineSchema) }).optional(),
        suggestions: z.array(z.string()).optional(),
      })
    )
    .optional(),
  openings: z
    .array(
      z.looseObject({
        id: z.string(),
        title: z.string(),
        description: z.string().optional(),
        place: z.string().optional(),
        chapterIndex: z.number().optional(),
        segments: z
          .array(
            z.looseObject({
              type: z.string(),
              text: z.string().optional(),
              speaker: z.string().optional(),
              speakerId: z.string().nullable().optional(),
              image: z.string().optional(),
            })
          )
          .optional(),
      })
    )
    .default([]),
  personas: z
    .array(
      z.looseObject({
        name: z.string(),
        role: z.string().optional(),
        desc: z.string().optional(),
        description: z.string().optional(),
        gender: z.string().optional(),
        image: z.string().optional(),
      })
    )
    .optional(),
  stats: z
    .looseObject({
      likes: z.number().optional(),
      chats: z.number().optional(),
      views: z.number().optional(),
      comments: z.number().optional(),
      week: z
        .union([
          z.number(),
          z.looseObject({
            likes: z.number().optional(),
            chats: z.number().optional(),
          }),
        ])
        .optional(),
    })
    .optional(),
  tags: z.array(z.string()).optional(),
  stages: z
    .array(
      z.looseObject({
        k: z.string(),
        min: z.number(),
        label: z.string(),
        color: z.string().optional(),
        tone: z.string().optional(),
        call: z.string().optional(),
        line: z.string().optional(),
      })
    )
    .optional(),
  initialStatus: z
    .looseObject({
      date: z.string().optional(),
      minutes: z.number().nullable().optional(),
      time: z.number().nullable().optional(),
      noTime: z.boolean().optional(),
      place: z.string().optional(),
      weather: z.string().optional(),
      expression: z.string().optional(),
      outfit: z.string().optional(),
      inner: z.string().optional(),
    })
    .optional(),
  initialAffinity: z.number().optional(),
  initialPlace: z.string().optional(),
  places: z
    .array(
      z.union([
        z.string(),
        z.looseObject({ name: z.string(), desc: z.string().optional() }),
      ])
    )
    .optional(),
  cards: z
    .array(
      z.looseObject({
        id: z.string(),
        image: z.string().optional(),
        locked: z.boolean().optional(),
        place: z.string().optional(),
        gate: z.record(z.string(), z.unknown()).nullable().optional(),
        adult: z.boolean().optional(),
      })
    )
    .optional(),
})

/**
 * /catalog 응답 envelope 검증.
 * 작품 뷰모델은 수백 개 필드라 전부를 스키마로 옮기지 않고, 화면이 기대는 뼈대(목록·작품 id·이름·이미지 표)만
 * 경계에서 확인한다. 세부 모양은 features/veluvit/types/catalog.types.ts 가 문서 역할을 한다.
 */
export const catalogEnvelopeSchema = z.looseObject({
  order: z.array(z.string()),
  chars: z.record(
    z.string(),
    z.looseObject({
      id: z.string(),
      name: z.string(),
      lane: z.enum(['a', 'f', 'm']),
    })
  ),
  stages: z.array(
    z.looseObject({ k: z.string(), min: z.number(), label: z.string() })
  ),
  images: z.record(z.string(), z.record(z.string(), z.string())),
  works: z.array(z.looseObject({ id: z.string(), name: z.string() })),
})
